<script setup lang="ts">
  import { computed, onBeforeMount, ref } from 'vue';

  import type { Partner } from '@stuut/api/models';
  import { Collapsible, CollapsibleContent } from '@ui/components/collapsible';
  import { Skeleton } from '@ui/components/skeleton';
  import { toast } from '@ui/components/sonner';

  import NoteCard from '@/components/notes/NoteCard.vue';
  import NoteEditorDialog from '@/components/notes/NoteEditorDialog.vue';
  import Pagination from '@/components/pagination/Pagination.vue';
  import { useNotes } from '@/store/notes';

  interface Props {
    partner: Partner;
  }

  const props = defineProps<Props>();
  const isEditorOpen = defineModel<boolean>({ default: false });
  const isLoading = ref(false);
  const isShowAll = ref(false);

  const NUM_ITEMS_PER_PAGE = 10;
  const notesStore = useNotes();

  function showAll() {
    isShowAll.value = true;
  }

  function showLess() {
    isShowAll.value = false;
  }

  const hasMore = computed(() => notesStore.notes.length > 2);

  defineExpose({ isShowAll, showAll, showLess, hasMore });

  const currentPage = computed({
    get: () => notesStore.page,
    set: (value) => notesStore.fetchNotesByPage(value),
  });

  const itemsPerPage = computed(() => notesStore.numItemsPerPage);
  const totalItems = computed(() => notesStore.numTotalItems);
  const notesInPage = computed(() => notesStore.notes);

  onBeforeMount(async () => {
    try {
      isLoading.value = true;
      await notesStore.init(NUM_ITEMS_PER_PAGE, {
        partner: props.partner.id,
      });
      await notesStore.fetchNotesByPage(currentPage.value);
    } catch (error: any) {
      toast.error(error.clientMessage);
    } finally {
      isLoading.value = false;
    }
  });
</script>

<template>
  <div class="flex flex-col gap-y-2" v-bind="$attrs">
    <template v-if="isLoading">
      <div v-for="i in 3" :key="i" class="flex py-2">
        <Skeleton class="w-full h-6" />
      </div>
    </template>

    <span v-if="!isLoading && !notesInPage.length" class="text-sm text-muted-foreground">
      <span>You haven't added any notes for this customer yet.</span>
    </span>

    <Collapsible v-if="!isLoading && notesInPage.length" v-model:open="isShowAll">
      <NoteCard v-for="note in notesInPage.slice(0, 2)" :key="note.id" :note="note" />

      <CollapsibleContent>
        <NoteCard v-for="note in notesInPage.slice(2)" :key="note.id" :note="note" />
      </CollapsibleContent>
    </Collapsible>

    <div class="flex justify-start">
      <span class="flex items-center text-sm gap-x-2 cursor-pointer" @click="isEditorOpen = true">
        <i class="far fa-plus-circle" />
        <span>New Note</span>
      </span>
    </div>
  </div>

  <Pagination
    v-if="totalItems > itemsPerPage"
    v-model:page="currentPage"
    :total="totalItems"
    :limit="itemsPerPage"
  />

  <NoteEditorDialog
    v-model:is-open="isEditorOpen"
    :create-note-for-partner-props="{ partnerId: partner.id }"
  />
</template>

<style scoped></style>
